<template>
  <Transition
    enter-active-class="transition-opacity duration-300"
    enter-from-class="opacity-0"
    enter-to-class="opacity-100"
    leave-active-class="transition-opacity duration-300"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="modelValue"
      class="fixed inset-0 z-[1001] flex items-center justify-center p-4"
    >
      <!-- 背景遮罩 -->
      <div 
        class="absolute inset-0 bg-black/60 backdrop-blur-sm"
        @click="$emit('update:modelValue', false)"
      />
      
      <!-- 登录框 -->
      <div class="relative bg-white/95 backdrop-blur-md rounded-xl p-8 w-full max-w-md border border-blue-100 shadow-lg shadow-blue-500/10 z-[1002]">
        <!-- 关闭按钮 -->
        <button 
          class="absolute top-4 right-4 text-gray-600 hover:text-blue-600 transition-colors duration-200" 
          @click="$emit('update:modelValue', false)"
        >
          <i class="fas fa-times" />
        </button>
        
        <!-- 登录标题和提示文字 -->
        <div class="text-center mb-8">
          <h2 class="text-2xl font-bold bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent">
            {{ loginMsg.title }}
          </h2>
          <p class="text-gray-600 mt-2">
            {{ loginMsg.subtitle }}
          </p>
          <p class="text-gray-500 text-sm mt-3">
            {{ loginMsg.memberTip }}
          </p>
        </div>
        
        <!-- 社交账号登录 -->
        <div class="space-y-4">
          <button 
            v-for="loginType in loginTypes" 
            :key="loginType.id"
            class="w-full py-3 px-4 flex items-center justify-center rounded-lg border transition-all duration-200"
            :class="{
              'opacity-60 cursor-not-allowed border-gray-200 bg-gray-100': loading,
              'border-gray-200 hover:border-blue-400 hover:bg-gray-50 text-gray-700 hover:text-blue-600': !loading
            }"
            :disabled="loading"
            @click="handleSocialLogin(loginType.providerMap)"
          >
            <!-- 当前按钮正在加载显示旋转图标 -->
            <i
              v-if="loading && currentProvider === loginType.providerMap"
              class="fas fa-spinner fa-spin mr-3 text-xl text-blue-500"
            />
            <!-- 其他按钮或未加载状态显示原图标 -->
            <i
              v-else
              :class="[loginType.icon, 'text-xl mr-3', loading ? 'text-gray-400' : '']"
            />
            <span :class="{ 'text-gray-400': loading }">{{ loginType.desc }}</span>
          </button>
        </div>
        
        <!-- 错误信息显示 - 更醒目的样式 -->
        <transition
          enter-active-class="transition-all duration-300"
          enter-from-class="opacity-0 transform -translate-y-4"
          enter-to-class="opacity-100 transform translate-y-0"
          leave-active-class="transition-all duration-300"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div 
            v-if="error" 
            class="bg-red-50 text-red-600 px-4 py-4 rounded-lg mt-6 text-center border border-red-200 shadow-sm flex items-center"
          >
            <i class="fas fa-exclamation-circle text-xl mr-2 text-red-500 flex-shrink-0"></i>
            <span>{{ error }}</span>
          </div>
        </transition>
      </div>
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { AuthApi, GetLoginUrlRequest, UserProviderMap } from '@/api/auth';
import { useI18nMessages } from '@/i18n';

// 组件 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true
  },
  redirect: {
    type: String,
    default: ''
  }
});

// 组件事件
const emit = defineEmits(['update:modelValue', 'loginSuccess']);

// 获取登录相关的国际化消息
const loginMsg = useI18nMessages('Login');

// 状态
const loading = ref(false);
const error = ref('');
const currentProvider = ref<number | null>(null);


// 检查UserProviderMap是否正确导入
if (!UserProviderMap) {
  error.value = loginMsg.value?.error?.serverError;
}

const loginTypes = computed(() => {
  const messages = loginMsg.value;
  
  // 如果UserProviderMap未定义，返回空数组
  if (!UserProviderMap) {
    return [];
  }
  
  return [
    {
      id: messages?.types?.[0]?.id,
      provider: messages?.types?.[0]?.provider,
      icon: messages?.types?.[0]?.icon,
      desc: messages?.types?.[0]?.desc,
      providerMap: UserProviderMap?.github, // 添加空值检查和回退值
    },
    {
      id: messages?.types?.[1]?.id,
      provider: messages?.types?.[1]?.provider,
      icon: messages?.types?.[1]?.icon,
      desc: messages?.types?.[1]?.desc,
      providerMap: UserProviderMap?.google, // 添加空值检查和回退值
    },
  ];
});

const handleSocialLogin = async (providerMap: number) => {
  if (loading.value) return;
  loading.value = true;
  error.value = '';
  currentProvider.value = providerMap;
  try {    
    const request: GetLoginUrlRequest = {
      provider: providerMap,
    };
    const response = await AuthApi.getLoginUrl(request);
    if (response.code === 0 && response.data.url) {
      // 根据URL是否包含查询参数选择正确的拼接方式
      const loginUrlWithState = response.data.url.includes('?')
        ? `${response.data.url}&state=${response.data.state}`
        : `${response.data.url}?state=${response.data.state}`;
      window.location.href = loginUrlWithState;
    } else {
      error.value = loginMsg.value?.error?.serverError;
      // 服务器错误也延迟关闭loading状态，确保错误信息可见
      setTimeout(() => {
        loading.value = false;
      }, 3000);
      return;
    }
  } catch (err: any) {
    error.value = loginMsg.value?.error?.networkError;
    // 保持错误信息显示，避免闪烁
    setTimeout(() => {
      loading.value = false;
    }, 8000); // 延长到8秒，给用户足够时间看到错误信息
    return; // 防止执行finally中的代码
  } finally {
    if (!error.value) { // 只有在没有错误时才立即关闭加载状态
      loading.value = false;
    }
  }
};
</script>

<style scoped>
/* 登录弹窗样式 */
</style> 